import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogButtons } from '../../common/components/Modal/Modal';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../components/Buttons';
import { FormSummary } from '../components/FormSummary';
import { SlotForm } from '../components/SlotForm';
import { TraceForm } from '../components/TraceForm';
import { Report } from '../models/Report';
import { Slot } from '../models/Slot';
import { SlotType } from '../models/SlotType';
import { SlotService } from '../services/SlotService';

enum Steps {
  SLOT_DATA = 1,
  SLOT_CREATED = 2,
  UPLOAD_TRACE = 3,
  REPORT_CREATED = 4,
}

type Props = {
  projectId: number;
  slotTypes: SlotType[];
  onClosed: (dismissed: boolean, slotId: number | undefined, reportId: number | undefined) => void;
};

export const CreateSlotFlow = ({ projectId, slotTypes, onClosed }: Props) => {
  // It's not a preference to have services in states, but we use it here so
  // that we can leverage same service in multiple functionalities.
  const [slotService] = useState<SlotService>(new SlotService());

  const [step, setStep] = useState<Steps>(Steps.SLOT_DATA);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [report, setReport] = useState<Report | null>(null);

  useEffect(() => {
    // Cancel all active requests on unmount
    return () => slotService.cancelAllRequests();
  }, [slotService]);

  const onSlotCreated = (s: Slot) => {
    setSlot(s);
    setStep(Steps.SLOT_CREATED);
  };

  const onCreateAnotherSlot = () => {
    setSlot(null);
    setReport(null);
    setStep(Steps.SLOT_DATA);
  };

  const onUploadTrace = () => {
    setStep(Steps.UPLOAD_TRACE);
  };

  const onReportCreated = (r: Report) => {
    setReport(r);
    setStep(Steps.REPORT_CREATED);
  };

  const STEP_MAP = {
    [Steps.SLOT_DATA]: {
      title: t('createSlot.slot.title'),
      subtitle: t('createSlot.slot.subtitle'),
      component: () => (
        <SlotForm
          submit={{
            label: t('createSlot.slot.submit'),
            action: (name, description, slotType, tags) => slotService.createSlot(projectId, name, description, slotType, tags).then(onSlotCreated),
          }}
          back={{ label: t('createSlot.slot.back'), action: () => onClosed(false, undefined, undefined) }}
          slotTypes={slotTypes}
        />
      ),
      actions: null,
    },
    [Steps.SLOT_CREATED]: {
      title: t('createSlot.slotDone.title', { slotName: slot?.getName() }),
      subtitle: t('createSlot.slotDone.subtitle'),
      component: () => <FormSummary slot={slot} />,
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(false, slot?.getId(), undefined)}>{t('createSlot.slotDone.back')}</TertiaryButton>
          <SecondaryButton onClick={onCreateAnotherSlot}>{t('createSlot.slotDone.another')}</SecondaryButton>
          <PrimaryButton autoFocus onClick={onUploadTrace}>
            {t('createSlot.slotDone.submit')}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
    [Steps.UPLOAD_TRACE]: {
      title: t('createSlot.upload.title'),
      subtitle: t('createSlot.upload.subtitle'),
      component: () => <TraceForm projectId={projectId} slotId={slot!.getId()} onDone={onReportCreated} />,
      actions: null,
    },
    [Steps.REPORT_CREATED]: {
      title: t('createSlot.uploadDone.title', { reportName: report?.getName() }),
      subtitle: t('createSlot.uploadDone.subtitle'),
      component: () => <FormSummary slot={slot} report={report} />,
      actions: () => (
        <DialogButtons>
          <TertiaryButton onClick={() => onClosed(true, slot?.getId(), report?.getId())}>{t('createSlot.uploadDone.back')}</TertiaryButton>
          <SecondaryButton onClick={onCreateAnotherSlot}>{t('createSlot.uploadDone.another')}</SecondaryButton>
          <PrimaryButton autoFocus onClick={() => onClosed(false, slot?.getId(), report?.getId())}>
            {t('createSlot.uploadDone.submit')}
          </PrimaryButton>
        </DialogButtons>
      ),
    },
  };

  return (
    <Dialog title={STEP_MAP[step].title} subtitle={STEP_MAP[step].subtitle} onDismiss={() => onClosed(true, slot?.getId(), report?.getId())}>
      <DialogBody>{STEP_MAP[step].component()}</DialogBody>
      {STEP_MAP[step].actions?.()}
    </Dialog>
  );
};
